// Reading a customer list from a spreadsheet or another app's export (Liam,
// 2026-09-26: "Start with sign up page and import"; onboarding must be "super
// clear and easy"). Pure functions only, no screen: the import screen
// (screens/import.ts) reads the file into rows of text, and this works out
// where the headings are, which column is which, and what each customer is.
//
// People keep customers every way imaginable: one "Name" column or "First
// name" and "Surname"; the address in one cell or spread over "Address 1",
// "Address 2" and "Town"; "£20", "20.00" or "20"; "4 weekly", "every 8 weeks"
// or "Monthly". So several columns may feed one box (names joined with a
// space, address parts with a comma), and anything unreadable is kept as text
// rather than guessed.

export type ImportField =
  | 'skip'
  | 'name'
  | 'first_name'
  | 'last_name'
  | 'address'
  | 'postcode'
  | 'phone'
  | 'email'
  | 'maps_link'
  | 'preferred_times'
  | 'visit_date'
  | 'area'
  | 'price'
  | 'cycle'
  | 'job_notes'
  | 'notes'
  | 'next_visit'
  | 'worker'

export const FIELD_LABELS: Record<ImportField, string> = {
  skip: "Don't import",
  name: 'Name',
  first_name: 'First name',
  last_name: 'Surname',
  address: 'Address',
  postcode: 'Postcode',
  phone: 'Phone',
  email: 'Email',
  maps_link: 'Google Maps link',
  preferred_times: 'Preferred times',
  visit_date: 'Past visit date',
  area: 'Area',
  price: 'Price',
  cycle: 'How often',
  // "Job notes", as the customer page calls it (2 Oct 2026).
  job_notes: 'Job notes',
  notes: 'Notes',
  next_visit: 'Next visit (book it)',
  worker: 'Worker',
}

// One customer as POST /api/customers/import takes it.
// One plain mailbox, the same rule as src/contact.ts EMAIL_RULE and the server (kept here so this file stays
// standalone).
const IMPORT_EMAIL_RULE = /^[A-Za-z0-9._+'-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/

export interface ImportRow {
  name: string
  address?: string
  postcode?: string
  phone?: string
  // The first plain email address in the row (0036); any others stay in notes.
  email?: string
  area?: string
  reg_price_pence?: number
  cycle_days?: number
  cycle_text?: string
  job_notes?: string
  notes?: string
  maps_link?: string
  preferred_times?: string
  // The latest past visit found in the sheet (28 Sep 2026): due dates work from
  // day one. Whether it was paid is asked once on the import screen.
  last_visit?: { date: string; amount_pence?: number }
  // The next visit already arranged in the app they are leaving (Squeegee's Next Due, today or later;
  // Liam 2 Oct 2026: "a note doesn't work reliably"): booked when they come in, agreed, at their price,
  // with the worker the file names when that is a worker on the round (the server decides).
  next_visit?: { date: string; worker?: string }
}

// ---- reading a CSV file ------------------------------------------------------

// Comma, semicolon or tab, whichever the first lines use most; quoted cells
// may hold commas, quotes ("") and line breaks.
export function parseCsv(text: string): string[][] {
  let clean = text.replace(/^\uFEFF/, '')
  // Excel's "sep=;" first line names the delimiter outright.
  const sep = clean.match(/^sep=(.)\r?\n/i)
  if (sep) clean = clean.slice(sep[0].length)
  // Otherwise whichever of comma, semicolon or tab appears most, counted
  // outside quotes (a semicolon file with commas in its notes stays a
  // semicolon file).
  const count = (ch: string): number => {
    let n = 0
    let q = false
    for (const c of clean.slice(0, 5000)) {
      if (c === '"') q = !q
      else if (!q && c === ch) n++
    }
    return n
  }
  const delimiter = sep ? sep[1] : [',', ';', '\t'].sort((a, b) => count(b) - count(a))[0]
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let quoted = false
  for (let i = 0; i < clean.length; i++) {
    const c = clean[i]
    if (quoted) {
      if (c === '"' && clean[i + 1] === '"') {
        cell += '"'
        i++
      } else if (c === '"') {
        quoted = false
      } else {
        cell += c
      }
    } else if (c === '"' && cell === '') {
      quoted = true
    } else if (c === delimiter) {
      row.push(cell)
      cell = ''
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && clean[i + 1] === '\n') i++
      row.push(cell)
      rows.push(row)
      row = []
      cell = ''
    } else {
      cell += c
    }
  }
  if (cell !== '' || row.length > 0) {
    row.push(cell)
    rows.push(row)
  }
  return tidyRows(rows)
}

// Trims every cell, drops rows with nothing in them, and squares the table off.
export function tidyRows(rows: unknown[][]): string[][] {
  const out = rows
    // A leading ' before = + - @ is the guard a spreadsheet export (Roundly's
    // own "Download your customers" included) puts on "+44 ..." so Excel does
    // not run it as a formula: the customer's value is what follows it.
    .map((r) => r.map((c) => (c === null || c === undefined ? '' : String(c)).trim().replace(/^'(?=[=+\-@])/, '')))
    .filter((r) => r.some((c) => c !== ''))
  const width = Math.max(0, ...out.map((r) => r.length))
  return out.map((r) => [...r, ...Array(width - r.length).fill('')])
}

// ---- which row holds the headings, and which column is which -----------------

// Headings in other languages, whole words only (set 4 had Dutch and German sheets).
// Never a word that is also an English heading ("Via" is where a lead came from,
// "Nr", "CAP", "Note", "Client"): Astra audit, "Via" became the address.
const OTHER_LANGUAGES: [ImportField, RegExp][] = [
  ['name', /^(naam|klant|kunde|nazwa|klient|imię i nazwisko|nome|cliente|nombre|nume)$/],
  ['first_name', /^(voornaam|vorname|imię|imie|prénom|prenom|nome próprio|primeiro nome|prenume)$/],
  ['last_name', /^(achternaam|nachname|familienname|nazwisko|apelido|apellido|nom de famille|cognome)$/],
  ['address', /^(adres|adresse|straat|straße|strasse|ulica|rua|calle|rue|strada|huisnummer|hausnummer|numer|número|plaats|stadt|miasto|cidade|ciudad|ville|città|citta|oraș|oras|woonplaats)$/],
  ['postcode', /^(postleitzahl|plz|kod pocztowy|código postal|codigo postal|code postal|cod postal)$/],
  ['phone', /^(telefoon|telefon|telefone|teléfono|telefono|téléphone|mobiel|handy|komórka|komorka|celular|móvil|movil)$/],
  ['price', /^(prijs|preis|cena|preço|preco|precio|prix|prezzo|preț|pret|tarief|tarif)$/],
  ['cycle', /^(frequentie|häufigkeit|haufigkeit|intervall|częstotliwość|czestotliwosc|frequência|frequencia|frecuencia|fréquence|frequence|frequenza|frecvență|frecventa)$/],
  ['area', /^(gebied|wijk|bezirk|gebiet|rejon|obszar|zona|área|quartier|secteur|cartier)$/],
  ['visit_date', /^(laatst|laatste bezoek|letzter besuch|ostatnia wizyta|última visita|ultima visita|dernière visite|ultima vizită)$/],
  ['notes', /^(notitie|notities|opmerking|notiz|kommentar|bemerkung|uwagi|notatki|notas|observações|remarque|commentaire|observații)$/],
]

const HEADING_WORDS: [ImportField, RegExp][] = [
  ['first_name', /^(first\s*name|forename|given\s*name|christian\s*name|first)$/],
  ['last_name', /^(last\s*name|surname|family\s*name|second\s*name|last)$/],
  ['name', /^(name|customer|cust|customer\s*name|client|client\s*name|full\s*name|contact|contact\s*name|account)$/],
  ['postcode', /^(post\s*code|postal\s*code|zip|zip\s*code|pc|pcode|p\/?\s*code|post\s*cd)$/],
  ['address', /^(address|street|road|house|house\s*name|property|address\s*line\s*\d|address\s*\d|line\s*\d|town|city|village|county|locality|street\s*address|house\s*number)$/],
  ['phone', /^(phone|mobile|tel|telephone|phone\s*number|mobile\s*number|contact\s*number|cell|mob)$/],
  ['email', /^(e-?mail|email\s*address)$/],
  ['maps_link', /^((google\s*)?maps?(\s*link)?|location|pin|map\s*pin|google\s*maps?\s*link)$/],
  ['area', /^(area|round|zone|route|patch)$/],
  ['price', /^(price|cost|amount|charge|fee|£|\$|rate|price\s*[£$]|regular\s*price|clean\s*price|job\s*price|regular\s*clean|reg\s*clean|regular|normal\s*clean)$/],
  ['cycle', /^(frequency|freq|cycle|how\s*often|interval|weeks|wks?|every|period|schedule|round\s*frequency|clean\s*frequency|repeat|repeats|regularity)$/],
  ['job_notes', /^(access|access\s*notes|gate\s*code|key|parking|instructions|job\s*notes|directions)$/],
  ['notes', /^(notes?|comments?|info|information|remarks?|details)$/],
]

// A column of past visits ("Job Date (Oct)", "Price on the day (Nov 25)", "Last
// clean"): Roundly cannot bring visit history in yet, so these are left out
// rather than guessed as an address or piled into notes (Liam's Enfield sheet,
// 28 Sep 2026). The import screen says why under each one.
const MONTH_WORD = '(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*'
export function isHistoryHeading(cell: string): boolean {
  const t = cell.toLowerCase().replace(/[*:_]/g, ' ').replace(/\s+/g, ' ').trim()
  if (!t) return false
  if (/\b(job|clean|visit|service|work)\s*date\b|\bdate\s*(done|cleaned|of\s*(clean|visit))\b|^date\b|\blast\s*(clean|visit|done)\b|\bon the day\b|\bpaid\s*on\b/.test(t)) return true
  // A month and year in the heading: "(Oct 25)", "Nov 2025", "Jan-26".
  return new RegExp(`\\(?\\b${MONTH_WORD}[\\s'-]*\\d{2,4}\\)?`).test(t) || new RegExp(`\\(${MONTH_WORD}\\)`).test(t)
}

// A column that says when a clean was done: "Job Date (Oct)", "Last clean",
// "Date cleaned". Its dates give each customer's last visit. Prices on the day,
// "paid on" dates and next bookings are not visits and stay left out.
export function isVisitDateHeading(cell: string): boolean {
  const t = cell.toLowerCase().replace(/[()*:_]/g, ' ').replace(/\s+/g, ' ').trim()
  if (!t || /\bnext\b|\bdue\b|\bpaid\b|\bbook(ed|ing)?\b/.test(t)) return false
  return /\b(job|clean|cleaning|visit|service|work)\s*date\b|\bdate\s*(done|cleaned|completed|of\s*(clean|visit|job))\b|\b(last|previous|prev)\s*(clean(ed)?|visit(ed)?|done|job|completed|washed|serviced|service|attended)\b/.test(t) ||
    // "July date", "Aug clean": a month's column of clean dates.
    new RegExp(`^${MONTH_WORD}\\s*(date|clean(ed)?|done)$`).test(t)
}

function headingField(cell: string): ImportField | null {
  // "PaymentMethod", "JobNotes": words run together are split first.
  const t = cell.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase().replace(/[()*:_?]/g, ' ').replace(/\s+/g, ' ').trim()
  if (!t) return null
  // A clear field word beats the broad notes rules below (Astra audit 28 Sep
  // 2026: "Service frequency" became notes; live read it as how often).
  const fieldWord = /frequen|cycle|how\s*often|interval|\brepeat|\bpattern\b|\bspacing\b|price|cost|\bcharge|\bfee\b|£|\$/.test(t)
  if (isVisitDateHeading(cell)) return 'visit_date'
  if (isHistoryHeading(cell)) return 'skip'
  if (/\btime\s*taken\b|\bduration\b|\bhow\s*long\b|\bminutes\b|\bmins\b|\bhours\b/.test(t)) return 'skip'
  if ((/\bprefer+ed\s*(times?|days?|window|slot|visit|hours?|day\s*\/\s*time)\b|\bbest\s*times?\b|\bavailability\b|\btime\s*(pref|preference|wanted)\b|^preferences?$/.test(t)) && !/\b(pay|payment|method|contact)\b/.test(t)) return 'preferred_times'
  for (const [field, re] of HEADING_WORDS) if (re.test(t)) return field
  for (const [field, re] of OTHER_LANGUAGES) if (re.test(t)) return field
  // After the exact words, in this order (each rule from a real or test sheet,
  // 28 Sep 2026): what is never a customer's own detail; then the details whose
  // words are clearest (postcode, email, phone); names given with the address;
  // the address in parts; the area; notes for the cleaner; preferred times; the
  // price and how often; names; notes. The order matters: "Service postcode" is
  // a postcode, "Customer notes" and "Customer memo" are notes, not names.
  if (/\b(id|ref|reference|acc(ount)?\s*(no|num|number))\b/.test(t) || /^(is|has|do\s*not|don.?t)\b/.test(t)) return 'skip'
  // "House #", "Flat #", "Unit no": part of the address (Astra audit: the id rule took them).
  if (/\b(house|hse|flat|unit|door|property|building|apt|apartment|street|st)\s*(#|no|num|number)\b|\b(house|hse|flat|unit|door)\s*#/.test(t) || /\b(house|hse|flat|unit|door|property|building|apt|street)\s*#/.test(cell.toLowerCase())) return 'address'
  // "Phone #", "Mobile #": a number to call (Astra audit: the id rule took it).
  if (/#/.test(cell) && /\b(phone|mobile|tel|telephone|mob|cell|contact)\b/.test(t)) return 'phone'
  // "Client #", "Customer no.", "Job number", "Invoice code": an id, never the name (Astra audit).
  if (/#/.test(cell) || /\b(client|customer|cust|account|job|invoice|contract)\s*(no|num|number|code)\b/.test(t)) return 'skip'
  // How often they pay is not how often they are cleaned (Astra audit: "Payment frequency").
  if (/\b(pay|payment|billing|invoice|invoicing)\s*(frequency|interval|cycle|schedule|terms)\b/.test(t)) return 'notes'
  if (/\btime\s*allowed\b|\best\.?\s*(time|duration)\b|\bexpected\s*time\b/.test(t)) return 'skip'
  // Money owed, a billing or invoice address, how they heard: notes.
  if (/\b(balance|owes?|owed|arrears|debt|in\s*credit|credit\s*on\s*account|outstanding)\b/.test(t)) return 'notes'
  if (/^(invoice|invoice\s*(no|number|ref))$/.test(t)) return 'skip'
  if (/\b(billing|invoice|invoicing)\b/.test(t)) return 'notes'
  if (/\b(how\s*heard|lead\s*source|source|referr(al|ed)|found\s*us)\b|^via$/.test(t)) return 'notes'
  // Codex review 28 Sep 2026: "Week 1" is a rota column, "Property type" a
  // note, "Call before?" an instruction, "City & postcode" an address with
  // its postcode at the end (lifted out later).
  if (/^(week|wk)\s*\d+$|^(week|wk)\s*(no|number)$/.test(t)) return 'notes'
  if (/\bweek\s*(of|commencing|beginning)\b|\bw\s*\/\s*c\b/.test(t)) return 'skip'
  if (/\b(property|house|building)\s*type\b|\btype\s*of\s*(property|house)\b/.test(t)) return 'notes'
  if (/\b(call|ring|text|phone)\s*(before|ahead|first)\b/.test(t)) return 'job_notes'
  if (/\b(city|town|address)\b.*\b(post\s*code|postal\s*code)\b|\b(post\s*code|postal\s*code)\b.*\b(city|town|address)\b/.test(t)) return 'address'
  if (/post\s*code|postal\s*code|\bzip\b|\bpcode\b|^p\s*\/\s*c$/.test(t)) return 'postcode'
  if (/e-?mail/.test(t)) return 'email'
  // "Notes to add to whatsapp message" is notes, not a number.
  if (/phone|mobile|\btel\b|telephone|\bmob\b|whats\s*app|\bcell\b/.test(t) && !/\bnotes?\b|\bmessage\b|\btext\b/.test(t)) return 'phone'
  if (/^(ring|call|number\s*to\s*call|contact\s*(no|number))$/.test(t)) return 'phone'
  // "Who / where", "At / who", "Name & address": the customer, name and address together.
  if (/^(who|who\s*for|(who|person|customer|client|name)\s*(\/|&|and)\s*(where|doorstep|address)|(at|where)\s*(\/|&|and)\s*who)$/.test(t)) return 'name'
  // The address, in parts: "Unit", "Premises", "Hse No", "Addr 1", "Site line 1", "Locality", "County / region".
  if (/\b(service|property|site|job|cleaning)\s*(address|location)\b/.test(t)) return 'address'
  if (/\b(house|hse|door|property)\s*(no|num|number)\b/.test(t)) return 'address'
  if (/^(unit|flat|apt|apartment|suite|building|block|premises)\b|^site$|^site\s*\/\s*units?$/.test(t)) return 'address'
  if (/\b(addr|address|street|town|township|borough|parish|hamlet|road|house|property|premises|village|locality|county|region|district|city|line\s*\d|site\s*line|estate|neighbou?rhood)\b/.test(t)) return 'address'
  // The area or round: "Route / list", "Run", "Round / patch", "Round name".
  if (!fieldWord && (/^(area|round|zone|route|patch|run|walk|beat|tour)\b/.test(t) || /\b(job|service|work|cleaning|round|customer)\s*(area|zone|patch|route)\b/.test(t)) && !/frequen|price|cost|order|notes?\b|code/.test(t)) return 'area'
  // Who does the job ("Crew", "Team", "Van"): notes, never the area (set 5).
  if (/^(crew|team|van|operative|cleaner|worker|staff|assigned\s*to)$/.test(t)) return 'skip'
  // The person met on site ("On-site name", "Site contact"): not the customer when the site has its own name column.
  if (/^(on[\s-]?site\s*(name|contact)|site\s*contact|person\s*on\s*site)$/.test(t)) return 'notes'
  // Where a customer sits in the round, a week number or a tier: never the price.
  if (/^(order|seq|sequence|position|pos|week|week\s*no|wk\s*no|tier|band|no\s*of\s*windows|windows|round\s*order)$/.test(t)) return 'notes'
  // For the cleaner at the house.
  // ("Access equipment extra" is a price for extras: notes.)
  if (/\b(remember|note\s*to\s*self|route\s*notes?|directions|instructions|access|gate|key|parking|survey|entry|getting\s*in|pets?|alarm|requirements|work\s*required|consignes)\b/.test(t) && !/\b(extras?|price|charge|fee|cost)\b|£/.test(t)) return 'job_notes'
  // What to clean ("Service", "Job type", "Job description"): for the cleaner at the house.
  if (!fieldWord && /^(service|services|service\s*type|job\s*type|job\s*description|description|work|what\s*to\s*clean)$/.test(t)) return 'job_notes'
  if (!fieldWord && /\b(pay|pays|payment|paid\s*by|method|terms)\b/.test(t)) return 'notes'
  if (!fieldWord && /\b(service|services|company|business\s*type|job\s*type|job\s*description|description|firm|organi[sz]ation)\b/.test(t)) return 'notes'
  if (/^(extras?|status|state|live|active|other|other\s*bits|misc|sundries|labels?|tags?|rooflights?|skylights?|conservatory|conny|gutters?|fascias?|soffits?|frames?|sills?|progress)$/.test(t)) return 'notes'
  if (/\b(slot|suits|working\s*window|time\s*window|before\s*opening|when\s*to)\b/.test(t)) return 'preferred_times'
  // The price and how often: "Charge each time", "Usual fee", "Each visit", "Visit spacing", "Back in", "Weeks / months".
  if (/price|cost|£|\$|\bcharge|\bfee\b|\brate\b|\beach\s*(visit|clean|time)\b|\busual\b|\bcontract\b|\bamount\b/.test(t)) return 'price'
  if (/frequen|cycle|weekly|how\s*often|\brepeat|\bpattern\b|\bspacing\b|\binterval\b|\bback\s*in\b|\bweeks?\b|\bmonths?\b|\bevery\b/.test(t)) return 'cycle'
  if ((/(first|fore|given)/.test(t) && /name/.test(t)) || /^(given|forename|forenames|christian)$/.test(t)) return 'first_name'
  if (/^(family|surname|last)$/.test(t)) return 'last_name'
  if (/(sur|last|family)/.test(t) && /name/.test(t)) return 'last_name'
  if (/\b(notes?|memo|comments?|remarks?)\b/.test(t)) return 'notes'
  if (/name|customer|client/.test(t)) return 'name'
  return null
}

// The first of the top ten rows where at least two cells read as headings;
// -1 when the file has no headings at all (just customers).
// A cell that looks like a customer's details, not a heading: a price, a
// postcode, a phone number, "4 weekly", or anything with two digits in a row.
function looksLikeData(c: string): boolean {
  // A cycle is data only as the whole cell ("4 weekly"): "Price 4 weekly" is a
  // heading (Codex review 28 Sep 2026: it made the heading row a customer).
  // A how-often answer alone ("weekly", "adhoc") and a sentence ("Side gate
  // sticks; lift latch") are a customer's details, not headings.
  return MONEY_RE.test(c) || POSTCODE_RE.test(c) || PHONE_RE.test(c) || /^\s*(every\s*)?\d+\s*-?\s*(weekly|weeks?|wks?|w|months?|monthly|mths?|years?|yearly)\s*$/i.test(c) || /\d{2}/.test(c) || /[£$]\s*\d/.test(c) ||
    /^\s*(weekly|fortnightly|monthly|quarterly|yearly|annually|ad[\s-]?hoc|one[\s-]?off|on[\s-]?call)\s*$/i.test(c) || c.trim().split(/\s+/).length > 5 || /[;!?]/.test(c) ||
    // "6-8 weeks", "every 3 months", and a customer's status alone (set 4: a data row read as headings).
    (/\d.*\b(weeks?|wks?|months?|weekly|monthly)\b/i.test(c) && !/price|cost|charge|fee|£|\$|rate|amount/i.test(c)) || /^\s*(active|stopped|moved|paused|archived|cancelled|booked|done|quoted|declined)\s*$/i.test(c)
}

// Columns whose name, address, area or price Roundly worked out from the cells,
// or from a rule that overrode what the heading said ("Site" as the name,
// "District" as the area), rather than from a heading it knows for that. The
// import screen shows each guess and asks the owner to confirm it (Liam, 29 Sep
// 2026: "ask like you said when not sure").
export function guessedColumns(header: string[] | null, fields: ImportField[]): number[] {
  const KEY: ImportField[] = ['name', 'first_name', 'last_name', 'address', 'area', 'price']
  return fields.map((f, c) => (KEY.includes(f) && (!header || headingField(header[c] ?? '') !== f) ? c : -1)).filter((c) => c >= 0)
}

// With no headings, two one-word columns side by side both taken as the name
// ("Hart" | "Megan"): the import screen asks which is the first name.
export function nameOrderQuestion(header: string[] | null, fields: ImportField[]): number[] {
  if (header) return []
  const cols = fields.map((f, c) => (f === 'name' ? c : -1)).filter((c) => c >= 0)
  return cols.length === 2 && cols[1] === cols[0] + 1 ? cols : []
}

// A cell that only a customer row would hold: a phone number, a postcode, a
// price, or a street address. A year or a date alone ("Updated September 2026")
// is not one, so a note above the headings is not taken for a customer.
function customerDetail(c: string): boolean {
  const v = c.trim()
  return PHONE_RE.test(v) || POSTCODE_RE.test(v) || /[£$]\s*\d|^\d+[.,]\d{2}$/.test(v) || /^((flat|unit)\s*\w+,?\s*)?\d+[a-z]?\s+\p{L}{3,}/iu.test(v)
}

export function findHeaderRow(rows: string[][]): number {
  return headerChoice(rows).index
}

// The customers in a sheet once its headings row is known: every row after
// it, and the rows above it the owner ticked as customers (`keep`, by index).
// Rows above with two or more cells (a note, or a customer typed above the
// headings) are listed in `above` for the import screen to ask about, never
// dropped unseen (Codex review 28 Sep 2026). With each row's spreadsheet number.
export function bodyRows(rows: string[][], headerRow: number, keep: Set<number> = new Set()): { body: string[][]; lines: number[]; above: number[] } {
  const body: string[][] = []
  const lines: number[] = []
  const above: number[] = []
  rows.forEach((r, i) => {
    if (i === headerRow) return
    if (i < headerRow) {
      const n = r.filter((c) => c.trim()).length
      const headed = (rows[headerRow] ?? []).filter((c) => c.trim()).length
      if (n >= 2 && (n >= Math.ceil(headed / 2) || r.some(customerDetail)) && r.filter((c) => !looksLikeData(c) && headingField(c) !== null).length < 2) above.push(i)
      if (!keep.has(i)) return
    }
    body.push(r)
    lines.push(i + 1)
  })
  return { body, lines, above }
}

// Which row holds the column names, and whether Roundly is unsure (then the
// import screen asks). The row with the most recognised headings among the
// first ten wins, so a group row ("Contact", "Property") above the real
// headings, or a title row, is passed over (held-out test 28 Sep 2026). Unsure:
// no such row although the first row reads like words, only two headings on a
// wide row, another heading row further down, or customer rows above it.
export function headerChoice(rows: string[][]): { index: number; unsure: boolean } {
  const top = rows.slice(0, 10)
  const hits = top.map((r) => r.filter((c) => !looksLikeData(c) && headingField(c) !== null).length)
  // The first row with two or more; a group row straight above fuller
  // headings steps down to them. Never a later row over customer rows (Codex
  // review 28 Sep 2026: customers above a repeated heading row were dropped).
  let index = hits.findIndex((h) => h >= 2)
  let stepped = false
  // A group row repeats its words across the columns under it (merged cells).
  const repeats = (r: string[] | undefined): number => { const v = (r ?? []).map((c) => c.trim().toLowerCase()).filter(Boolean); return v.length - new Set(v).size }
  if (index >= 0 && ((hits[index + 1] ?? 0) > hits[index] || ((hits[index + 1] ?? 0) >= 2 && repeats(top[index]) >= 2 && repeats(top[index + 1]) < repeats(top[index])))) { index += 1; stepped = true }
  const filled = (r: string[] | undefined): number => (r ?? []).filter((c) => c.trim()).length
  const wordy = (r: string[] | undefined): number => (r ?? []).filter((c) => c.trim() && !looksLikeData(c)).length
  // A row above with two or more cells that is not itself headings (a group row like "Contact | Property" is).
  // Customer details can hold heading words too ("side access", "Neighbour
  // referral"): a later row counts as headings only when three quarters of it
  // are headings, and a row above only when it is as full as a customer row, not
  // a title or a note (Astra audit 28 Sep 2026: 8 of 24 sheets asked).
  const aboveLike = (r: string[]): boolean => filled(r) >= 2 && (filled(r) >= Math.ceil(filled(top[index]) / 2) || r.some(customerDetail))
  const dataAbove = index > 0 && top.slice(0, index).some((r, i) => hits[i] < 2 && aboveLike(r))
  const laterHeads = hits.some((h, i) => i > index && h >= 2 && h >= filled(top[i]) * 0.75)
  const unsure = index < 0
    ? rows.length > 1 && wordy(rows[0]) >= 2 && wordy(rows[0]) >= filled(rows[0]) - 1 && filled(rows[0]) >= 2
    : (hits[index] <= 2 && filled(top[index]) >= 5) || laterHeads || dataAbove || (stepped && hits[index - 1] >= hits[index] - 1)
  return { index, unsure }
}

export const POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i
const PHONE_RE = /^(\+?44\s?|0)\s?\d[\d\s()-]{8,13}$/
// A Google Maps link: the short share link or a full maps address. Only https,
// the same rule the customer page uses before it makes the link tappable.
const MAPS_LINK_RE = /^https:\/\/(maps\.app\.goo\.gl|goo\.gl\/maps|(www\.)?google\.[a-z.]+\/maps|maps\.google\.[a-z.]+)\S*$/i
const MONEY_RE = /^[£$]?\s*\d{1,4}([.,]\d{1,2})?$/
const DATE_RE = new RegExp(`^\\s*(\\d{1,2}(st|nd|rd|th)?[\\s/.-]*${MONTH_WORD}\\.?([\\s/.,-]*\\d{2,4})?|${MONTH_WORD}\\.?[\\s/.-]*\\d{1,2}(st|nd|rd|th)?(,?\\s*\\d{2,4})?|\\d{1,2}[/.-]\\d{1,2}([/.-]\\d{2,4})?|\\d{4}-\\d{2}-\\d{2})\\s*$`, 'i')
const DURATION_RE = /^\s*\d+(\.\d+)?\s*(m|min|mins|minutes|h|hr|hrs|hour|hours)\b/i
const CYCLE_RE = /(week|wk|month|fortnight|yearly|annual|adhoc|ad hoc|one off|\b\d+\s*w\b)/i

// What the cells under a column look like, when its heading didn't say.
function contentField(values: string[]): ImportField {
  const filled = values.filter((v) => v !== '')
  if (filled.length === 0) return 'skip'
  const share = (re: RegExp): number => filled.filter((v) => re.test(v)).length / filled.length
  if (share(POSTCODE_RE) >= 0.6) return 'postcode'
  if (share(PHONE_RE) >= 0.6) return 'phone'
  if (share(/@/) >= 0.6) return 'email'
  // A column of Google Maps links (28 Sep 2026, Liam's Enfield sheet).
  if (share(MAPS_LINK_RE) >= 0.6) return 'maps_link'
  // Small whole numbers ("1", "2", "4": a tier, a week number, an order in the
  // round) are not prices: Liam's Work list "Tier" became the price (28 Sep 2026).
  // A £10 or £12 price is still a price (Codex review 28 Sep 2026): only numbers
  // that start low (a 1, 2 or 3 among them) count as a tier or an order.
  const ints = filled.filter((v) => /^\s*\d{1,3}\s*$/.test(v)).map(Number)
  if (ints.length / filled.length >= 0.6 && Math.min(...ints) <= 3 && (Math.max(...ints) <= 12 || new Set(ints).size >= ints.length * 0.8)) return 'notes'
  // "020", "0113": dialling codes and the like, never a price.
  if (share(/^\s*0\d/) >= 0.5) return 'notes'
  if (share(MONEY_RE) >= 0.6) return 'price'
  if (share(CYCLE_RE) >= 0.6) return 'cycle'
  // "16 Oct", "20/11/2025": a date, never an address. "25 mins", "1 hour": how
  // long a job took, never an address (both were, on Liam's Enfield sheet).
  if (share(DATE_RE) >= 0.6 || share(DURATION_RE) >= 0.6) return 'skip'
  // "17 Crescent Rise", "Flat 2, 5 Mill Lane", "12a High Street".
  if (share(/^\s*(flat\s*\w+,?\s*)?\d+[a-z]?,?\s+[a-z]{2,}/i) >= 0.6) return 'address'
  // Sentences ("Side gate code 1234", "Dog in garden, ring first") are worth keeping.
  const words = filled.map((v) => v.trim().split(/\s+/).length)
  if (words.reduce((a, b) => a + b, 0) / filled.length >= 3.5 && share(/[a-z]{3,}/i) >= 0.8) {
    // About getting in ("Side gate sticks", "Dog in garden, ring first", "Park by the garage"): access notes.
    return share(/\b(gate|dog|dogs|key|keys|park|parking|code|ring|knock|side|rear|back|access|latch|alarm|ladder|garage|drive)\b/i) >= 0.5 ? 'job_notes' : 'notes'
  }
  return 'skip'
}

// A field for every column: from its heading first, then from what is in it.
// Only one column gets each single field (name, postcode, phone, price, how
// often); later ones become notes. Address parts may be many.
export interface PriceCandidate { c: number; rank: number; filled: number; typical: number }

// The price-looking columns, most likely regular price first. By cells only,
// two columns about as full: the lower is the regular price (a first clean
// costs more than the ones after it; held-out test 2).
function rankPrices(prices: PriceCandidate[]): PriceCandidate[] {
  const byCells = (a: PriceCandidate, b: PriceCandidate): number =>
    Math.min(a.filled, b.filled) >= 0.8 * Math.max(a.filled, b.filled) ? a.typical - b.typical : b.filled - a.filled
  return prices.filter((x) => x.rank > 0).sort((a, b) => b.rank - a.rank || (a.rank === 1 ? byCells(a, b) : 0) || a.c - b.c)
}

// A "Region" column whose value repeats down the sheet and does not say which
// it is ("West End" on every row): part of the address, or the round's area?
// The import screen asks; until then it stays in the address (Astra audit).
export function regionQuestion(header: string[] | null, body: string[][], fields: ImportField[]): number {
  if (!header) return -1
  return header.findIndex((h, c) => {
    if (!/^\s*regions?\s*$/i.test(h ?? '') || fields[c] !== 'address') return false
    const vals = body.slice(0, 50).map((r) => (r[c] ?? '').trim().toLowerCase()).filter(Boolean)
    return vals.length >= 4 && new Set(vals).size <= Math.max(2, vals.length * 0.25) && !vals.every((v) => /shire\b/.test(v))
  })
}

// Two headed price columns equally likely to be the regular price ("Price" and
// "Ongoing £"): the import screen asks which, rather than choose one silently
// (Astra audit 28 Sep 2026: "Start-up £" was taken over "Ongoing £").
export function priceTie(header: string[] | null, body: string[][]): number[] {
  if (!header) return []
  const width = Math.max(header.length, ...body.slice(0, 1).map((r) => r.length))
  const cands: PriceCandidate[] = []
  for (let c = 0; c < width; c++) {
    const values = body.slice(0, 50).map((r) => r[c] ?? '')
    if (headingField(header[c] ?? '') !== 'price' && !(headingField(header[c] ?? '') === null && contentField(values) === 'price')) continue
    const amounts = values.map((v) => readPrice(v).pence).filter((p): p is number => p !== null).sort((a, b) => a - b)
    cands.push({ c, rank: priceRank(header[c] ?? '', headingField(header[c] ?? '') === 'price'), filled: values.filter((v) => v.trim()).length, typical: amounts.length ? amounts[Math.floor(amounts.length / 2)] : 0 })
  }
  const ranked = rankPrices(cands)
  // Ask unless the first says "regular" and the second does not.
  return ranked.length >= 2 && ranked[1].rank >= 2 && !(ranked[0].rank === 3 && ranked[1].rank < 3) ? ranked.filter((x) => x.rank >= 2).map((x) => x.c) : []
}

// How likely a price-looking column is the regular price: 0 never (a first,
// one-off, inside or extras price, a total, a quote, an old price), 1 by its
// cells only, 2 a price heading, 2.5 "maintenance"/"ongoing" and the like, 3 a
// heading that says regular.
function priceRank(heading: string, byHeading: boolean): number {
  // "Price inc gutters" is still the price: what it includes does not count.
  const h = heading.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase().replace(/\b(inc|incl|including|with|plus)\b.*$/, '')
  // A total for a month or a year is not the price of one clean.
  // "Monthly regular price", "Regular price / month", "Annual fee", "Price per year".
  if (/\b(monthly|annual|annually|yearly|month|year|annum)\b|\/\s*(mth|yr|wk)\b/.test(h)) return 0
  if (/\b(start[\s-]?up|set[\s-]?up|setup|opening|onboarding|trial|history|previous|prev|old|was|last)\b/.test(h)) return 0
  if (/\b(first|1st|2nd|initial|one[\s-]?off|ad[\s-]?hoc|inside|internal|in\s*&\s*out|extras?|add[\s-]?ons?|addon|alt|alternate|alternative|total|deposit|quoted?|quotes?|estimates?|paid|deep|scrub|gutters?|conservatory|roof|fascias?|soffits?|skylights?|frames?)\b/.test(h)) return 0
  // A month's column ("June amount", "Paid May") is one visit, not the regular price.
  if (/\b(jan|january|feb|february|mar|march|apr|april|may|jun|june|jul|july|aug|august|sep|sept|september|oct|october|nov|november|dec|december)\b/.test(h)) return 0
  if (!byHeading) return 1
  if (/\b(reg|regular|normal|standard|usual)\b/.test(h)) return 3
  // Likely but not certain ("Maintenance fee", "Ongoing £"): chosen over a
  // plain price heading, and the screen asks when another headed price is
  // there too (priceTie; Astra audit: never a silent choice between them).
  if (/\b(main|recurring|repeat|ongoing|maintenance|routine|each)\b/.test(h)) return 2.5
  return 2
}

// Roundly's own "Download your customers" (src/customer-export.ts): its last columns, what the customer owes now and
// their last, next booked and next due days, are a record to keep, not something to bring in. Left out, so the file
// comes back in as the customers it went out as (27 Oct 2026). Only on that file: another app's "Last cleaned" is
// still read as the customer's last visit.
const OWN_DOWNLOAD_SIGN = 'job conversations (json)'
const OWN_DOWNLOAD_RECORD = /^(owed now( \(.+\))?|last cleaned|next booked|next due)$/

export function guessFields(header: string[] | null, body: string[][]): ImportField[] {
  const width = Math.max(header?.length ?? 0, ...body.slice(0, 1).map((r) => r.length))
  const ownDownload = !!header?.some((h) => (h ?? '').toLowerCase().trim() === OWN_DOWNLOAD_SIGN)
  const fields: ImportField[] = []
  const used = new Set<ImportField>()
  // Phone and email may repeat (a landline and a mobile); these may not.
  const single: ImportField[] = ['name', 'first_name', 'last_name', 'postcode', 'cycle', 'area', 'maps_link']
  // The price is chosen after every column is read, from all the columns that
  // could be it (below).
  const prices: PriceCandidate[] = []
  const recognised: boolean[] = []
  for (let c = 0; c < width; c++) {
    const values = body.slice(0, 50).map((r) => r[c] ?? '')
    const heading = (header?.[c] ?? '').toLowerCase().replace(/[.:]/g, '').trim()
    const byHeading = header ? headingField(header[c] ?? '') : null
    recognised.push(byHeading !== null)
    // "Number", "No." or "Nr" could be a phone or a house number: the cells
    // decide, and codes ("C-1001") are neither (Astra audit).
    let f: ImportField =
      /^(no|nr|number|num|#)$/.test(heading)
        ? (contentField(values) === 'phone' ? 'phone' : values.filter((v) => v.trim()).every((v) => /^\s*\d{1,4}[a-z]?\s*$/i.test(v)) ? 'address' : 'skip')
        : byHeading || contentField(values)
    if (ownDownload && OWN_DOWNLOAD_RECORD.test(heading)) f = 'skip'
    // "Area code" over codes like "A1", "NE" (not phone codes like 020): the round's area (set 5).
    if (/^area\s*code$/i.test(heading) && values.some((v) => v.trim()) && values.filter((v) => v.trim()).every((v) => /^\s*[a-z]{1,3}\d{0,2}\s*$/i.test(v))) f = 'area'
    // "Region" that repeats down the sheet ("North Trade" on every row) is the area; a county stays the address.
    if (f === 'address' && /^regions?$/i.test(heading.trim())) {
      const vals = values.map((v) => v.trim().toLowerCase()).filter(Boolean)
      // ...unless it names a county or a UK region ("North Yorkshire", "Kent", "South East"): Astra audit.
      const COUNTYISH = /shire\b|\b(kent|essex|surrey|sussex|devon|cornwall|dorset|somerset|norfolk|suffolk|cumbria|durham|london|middlesex|merseyside|midlands|wales|scotland|east|west|north|south|central|greater)\b/i
      if (vals.length >= 4 && new Set(vals).size <= Math.max(2, vals.length * 0.25) && vals.every((v) => /trade|round|patch|run|zone|area/i.test(v))) f = 'area'
    }
    // "CALL" over cells with no numbers in them is not a phone column.
    if (f === 'phone' && byHeading === 'phone' && values.some((v) => v.trim()) && !values.some((v) => v.replace(/\D/g, '').length >= 6)) f = 'notes'
    // "Location" or "Pin" holding addresses, not map links: the cells decide.
    if (f === 'maps_link' && !/map|link/.test(heading) && contentField(values) !== 'maps_link') f = contentField(values) === 'skip' ? 'address' : contentField(values)
    // "Account" holding 12345: an account number, not a name (Codex review).
    if (f === 'name' && values.filter((v) => v.trim()).length && values.filter((v) => /^\s*[\d\s-]+\s*$/.test(v) || /^\s*[a-z]{0,5}[-\/]?\d[\w\/-]*\s*$/i.test(v)).length / values.filter((v) => v.trim()).length >= 0.6) f = 'skip'
    if (f === 'price') {
      const amounts = values.map((v) => readPrice(v).pence).filter((p): p is number => p !== null).sort((a, b) => a - b)
      prices.push({ c, rank: priceRank(header?.[c] ?? '', byHeading === 'price'), filled: values.filter((v) => v.trim()).length, typical: amounts.length ? amounts[Math.floor(amounts.length / 2)] : 0 })
    }
    else if (single.includes(f) && used.has(f)) f = 'notes'
    used.add(f)
    fields.push(f)
  }
  // Several columns that could be the price ("Inside Price", "Reg Clean Price",
  // "First Clean/Adhoc Price" on Liam's Work list; "Initial (£)" and "Regular
  // (£)" on New Jobs): the regular one is. A heading saying first, one-off,
  // inside, extras and the like is never it, even alone; among the rest a
  // heading beats the cells, "regular"/"reg"/"normal" beats a plain "Price";
  // then the first such heading, or, by cells only, the fuller column (extras
  // are mostly empty), or of two as full the lower. The others go to notes.
  const best = rankPrices(prices)[0]
  for (const x of prices) if (x !== best) fields[x.c] = 'notes'
  // "District" on a sheet that already has a town column is the round's area;
  // on its own it is part of the address (set 5: both kinds, by generator).
  if (header) {
    const district = header.findIndex((h, c) => /^\s*district\s*$/i.test(h ?? '') && fields[c] === 'address')
    const town = header.some((h, c) => c !== district && fields[c] === 'address' && /town|city|locality|village|place|plaats|ort/i.test(h ?? ''))
    if (district >= 0 && town && !fields.includes('area')) fields[district] = 'area'
  }
  // "Site" beside a real address column ("Premises", "Street") is the site's
  // name, not its address (set 5: "Clocktower Café" became the address).
  if (header && !fields.some((f) => f === 'name' || f === 'first_name' || f === 'last_name')) {
    const siteVals = (c: number): string[] => body.slice(0, 50).map((r) => (r[c] ?? '').trim()).filter(Boolean)
    const site = header.findIndex((h, c) => /^\s*site\s*$/i.test(h ?? '') && fields[c] === 'address' && siteVals(c).filter((v) => /^\d/.test(v)).length < siteVals(c).length * 0.3)
    const street = header.some((h, c) => c !== site && fields[c] === 'address' && /address|street|premises|road|line|house/i.test(h ?? ''))
    if (site >= 0 && street) fields[site] = 'name'
  }
  // No heading said "name": the first column with words in it most likely is.
  if (!fields.some((f) => f === 'name' || f === 'first_name' || f === 'last_name')) {
    // A column headed like a name (Company, Firm, Business name, Contact) first;
    // then the column whose words differ most from row to row, so a route or
    // area column ("North cottages" on every row) is never the name.
    const named = (c: number): boolean => /company|business|firm|organi[sz]ation|trading|name|client|customer|contact/i.test(header?.[c] ?? '')
    const variety = (c: number): number => { const v = body.slice(0, 50).map((r) => (r[c] ?? '').trim().toLowerCase()).filter(Boolean); return v.length ? new Set(v).size / v.length : 0 }
    const order = [...Array(width).keys()].sort((a, b) => Number(named(b)) - Number(named(a)) || Number(variety(b) >= 0.6) - Number(variety(a) >= 0.6) || a - b)
    for (const c of order) {
      if (fields[c] !== 'skip' && fields[c] !== 'notes') continue
      const values = body.slice(0, 50).map((r) => r[c] ?? '').filter(Boolean)
      // A column headed like a name may hold numbers too ("Quillmere Pantry Ltd — 12"); others must not look like addresses.
      const withAddress = (v: string): boolean => /^[^\d,]{2,60}\s+[—–-]\s+\S/.test(v) || /^[^,\d]{2,60},\s*((flat|unit|apt)\s*\w+,?\s*)?\d/i.test(v)
      const short = values.filter((v) => withAddress(v) || v.trim().split(/\s+/).length <= 4).length >= values.length * 0.8
      if (values.length && (short || named(c)) && values.filter((v) => /[a-z]/i.test(v) && (named(c) || withAddress(v) || !/\d{2,}/.test(v))).length / values.length >= 0.6) {
        fields[c] = 'name'
        break
      }
    }
  }
  // A column of one- to three-word names repeated down the sheet ("North loop"
  // on every row), with no heading Roundly knows: the area. A column of place
  // names beside the address: part of it (the town). Held-out test 3, 28 Sep 2026.
  const STATUSY = /^(active|stopped|moved|paused|live|archived|yes|no|y|n|cash|bacs|bank|card|cheque|gocardless|windows?|gutters?|fronts?|conservatory|monday|tuesday|wednesday|thursday|friday|saturday|sunday|new|quoted|booked|declined|won|lost|pending|enquiry|follow[\s-]?up|cancelled|confirmed|maybe)$/i
  // No headings, and the name column holds one word beside another one-word
  // column: the name is in two parts (set 5: "Hart", "Megan" came in as "Hart").
  if (!header) {
    const n = fields.indexOf('name')
    const oneWord = (c: number): string[] => body.slice(0, 50).map((r) => (r[c] ?? '').trim()).filter(Boolean)
    const isOne = (c: number): boolean => { const v = oneWord(c); return v.length >= 3 && v.every((x) => /^[\p{L}'-]+$/u.test(x)) && new Set(v.map((x) => x.toLowerCase())).size >= v.length * 0.6 }
    // Which is the first name cannot be told from the names themselves
    // ("Oliver", "Martin", "Robin" are both): both columns are the name in the
    // sheet's order, and the import screen asks (nameOrderQuestion; Astra audit).
    for (const k of [n + 1, n - 1]) {
      if (n < 0 || k < 0 || k >= width || fields[k] === 'name' || (fields[k] !== 'skip' && fields[k] !== 'notes') || !isOne(n) || !isOne(k)) continue
      fields[n] = 'name'
      fields[k] = 'name'
      break
    }
  }
  // An extra address line with no heading ("Flat 2", "Unit 3", "Rear cottage"),
  // straight after the address: short cells, half of them naming a flat or
  // unit with its number, or a dwelling ("Rear cottage", "Annexe"), never a
  // way in or a job ("Side gate", "Back door", "Front windows"): Astra audit.
  const DWELLING = /\b(cottage|annexe?|lodge|barn|bungalow|maisonette|studio|coach\s*house|mews\s*house|granny\s*flat)\b/i
  const WAY_IN = /\b(gate|access|door|windows?|garden|drive|path|entrance|alley|garage|key|code|dog)\b/i
  const PART = { test: (v: string): boolean => !WAY_IN.test(v) && (/^(flat|unit|apt|apartment|suite|floor|room)\s*[a-z]?\d+[a-z]?\b/i.test(v) || DWELLING.test(v)) }
  for (let c = 1; c < width; c++) {
    if (recognised[c] || fields[c] !== 'skip' || fields[c - 1] !== 'address') continue
    const vals = body.slice(0, 50).map((r) => (r[c] ?? '').trim()).filter(Boolean)
    if (vals.length && vals.every((v) => v.split(/\s+/).length <= 3) && vals.filter((v) => PART.test(v)).length >= vals.length * 0.5) fields[c] = 'address'
  }
  // Left to right, so a town then a county beside it both join the address.
  for (let c = 0; c < width; c++) {
    if (recognised[c] || (fields[c] !== 'skip' && fields[c] !== 'notes')) continue
    const vals = body.slice(0, 50).map((r) => (r[c] ?? '').trim()).filter(Boolean)
    if (vals.length < 4 || !vals.every((v) => /^[\p{L}' &-]+$/u.test(v) && v.split(/\s+/).length <= 3)) continue
    const distinct = new Set(vals.map((v) => v.toLowerCase())).size
    if (vals.some((v) => STATUSY.test(v))) continue
    // Beside the address, place names are the town; they repeat, or are one
    // word, where people's names ("Finn Alderwick") do neither (differential test).
    const besideAddress = fields[c - 1] === 'address' || fields[c + 1] === 'address'
    const townLike = distinct <= vals.length * 0.8 || vals.filter((v) => !/\s/.test(v)).length >= vals.length * 0.7
    const ROLE = /\b(manager|director|owner|assistant|supervisor|head|officer|admin|administrator|partner|lead|secretary|caretaker|receptionist|tenant|landlord|agent|mr|mrs|ms|miss|dr)\b/i
    // A place the address cells already say ("65 Orchard Place, East Dene" beside "East Dene") is the area.
    const addrCols = fields.map((f, k) => (f === 'address' && k !== c ? k : -1)).filter((k) => k >= 0)
    const echoed = body.slice(0, 50).filter((r) => { const v = (r[c] ?? '').trim().toLowerCase(); return v && addrCols.some((k) => (r[k] ?? '').toLowerCase().includes(v)) }).length
    if (vals.some((v) => ROLE.test(v))) continue
    if (besideAddress && townLike && echoed < vals.length * 0.5) fields[c] = 'address'
    else if (echoed >= vals.length * 0.5 && !fields.includes('area')) fields[c] = 'area'
    else if (distinct <= Math.max(3, vals.length * 0.25) && !fields.includes('area')) fields[c] = 'area'
  }
  // Squeegee's own export: every one of its headings is known (squeegeeField). A column it does not have
  // (one Squeegee adds later) keeps the guess above, and a guess that takes a field Squeegee's own columns
  // already fill goes to notes.
  if (header && isSqueegeeJobs(header)) {
    const known = squeegeeFields(header)
    const taken = new Set(known.filter((f): f is ImportField => f !== null))
    const one: ImportField[] = ['name', 'first_name', 'last_name', 'postcode', 'cycle', 'area', 'maps_link', 'price']
    return fields.map((f, c) => known[c] ?? (one.includes(f) && (taken.has(f) || (f !== 'name' && f.endsWith('_name') && taken.has('name'))) ? 'notes' : f))
  }
  return fields
}

// ---- Squeegee's "All job data" export (All_Jobs.csv) -------------------------------
// docs/import-jev/squeegee/README.md (2 Oct 2026). Insights, Reporting, All job data, Download gives
// one row per JOB, 27 columns. What each one is in Roundly:
//   Name, Phone, Alt Phone, Email, Round (the area), Price ("24.00"), Frequency ("every 4 weeks on
//   Friday"), Notes (the job's notes), Last Done (a date, or "never"): as their headings say.
//   Job Address: the address (where the cleaning is; it ends with the postcode). Address, or any other
//   address heading, is the customer's own: their billing address, kept in notes when it is different.
//   Services ("Window cleaning, Conservatory roof"): what to clean, for the cleaner at the house.
//   Next Due: booked as their next visit when it is today or later, with Assignee as its worker
//   (buildCustomers); a Next Due already past is kept in notes, and so is Assignee with no booking.
//   Left out: the ids (Job Ref, Customer Ref), Source, State, Job State, Is Quote, Created, Duration,
//   the counts (Occurrences, Not Done, Skipped, Done), and Balance (whether it is owed or in credit is
//   not said, so it is never guessed).
const SQUEEGEE: Record<string, ImportField> = {
  'job ref': 'skip', source: 'skip', 'customer ref': 'skip', name: 'name', phone: 'phone', 'alt phone': 'phone',
  email: 'email', state: 'skip', balance: 'skip', 'job state': 'skip', 'is quote': 'skip', 'job address': 'address', assignee: 'worker',
  created: 'skip', notes: 'notes', services: 'job_notes', frequency: 'cycle', round: 'area', price: 'price', duration: 'skip',
  occurrences: 'skip', 'not done': 'skip', skipped: 'skip', done: 'skip', 'last done': 'visit_date', 'next due': 'next_visit',
}
const squeegeeKey = (h: string | undefined): string => (h ?? '').toLowerCase().replace(/[_\s]+/g, ' ').trim()
const ADDRESS_HEADING = /\b(address|addr)\b/

// Where each Squeegee column goes, or null for a column Squeegee's export is not known to have. With a
// Job Address column, every other address heading ("Address", "Customer Address") is the billing address
// (squeegeeJobs puts it in notes when it differs); with none, "Address" is the address.
function squeegeeFields(header: string[]): (ImportField | null)[] {
  const keys = header.map(squeegeeKey)
  const job = keys.indexOf('job address')
  return keys.map((k, c) => (c === job ? 'address' : ADDRESS_HEADING.test(k) ? (job >= 0 ? 'skip' : 'address') : SQUEEGEE[k] ?? null))
}

// A file is Squeegee's job export when it has its own column names: Name, and four of Job Ref, Customer
// Ref, Job Address, Last Done and Next Due (no other app's export names its columns these words).
export function isSqueegeeJobs(header: string[] | null): boolean {
  if (!header) return false
  const has = new Set(header.map(squeegeeKey))
  return has.has('name') && ['job ref', 'customer ref', 'job address', 'last done', 'next due'].filter((k) => has.has(k)).length >= 4
}

// Squeegee lists each JOB on its own row, so a customer with windows and a conservatory roof is in the
// file twice, under the same Customer Ref. Roundly keeps one customer per house, with one price and one
// "how often": the jobs at one address come in as ONE customer.
//   The main job is the most frequent one (the first, of several as frequent). A job clearly done on the
//   same visit as it (the same frequency, day included, and the same Last Done and Next Due as every job
//   already counted in, where both say one) has its price added (£20 windows + £15 conservatory roof = £35 each visit).
//   Any other job (a roof every 26 weeks beside windows every 4; windows every 4 weeks on a Monday
//   beside a job every 4 weeks on a Friday) never joins the price: it is written in their notes ("Also
//   in Squeegee: Gutter clearing, £60.00, every 26 weeks on Monday, last done 4 May 2026, next due
//   2 Nov 2026"). Only the main job's Next Due is booked.
// Rows with no Customer Ref, or at a different job address (case and spaces aside), stay customers of
// their own. Each row's spreadsheet line is kept (the first job's, for one made of several). Notes, and
// any column Squeegee is not known to have, keep each different value of the jobs. Also, on every row:
// a row with no Job Address takes the billing address as the address, a billing address different from
// the Job Address goes in notes, and Squeegee's default "Window cleaning" service is dropped as saying
// nothing.
export interface SqueegeeJobs { body: string[][]; lines: number[]; joined: { line: number; name: string; jobs: number }[] }
export function squeegeeJobs(header: string[], body: string[][], lines: number[], today: string = new Date().toISOString().slice(0, 10)): SqueegeeJobs {
  const keys = header.map(squeegeeKey)
  const col = (k: string): number => keys.indexOf(k)
  const C = { ref: col('customer ref'), name: col('name'), jobAddress: col('job address'), notes: col('notes'), services: col('services'), frequency: col('frequency'), price: col('price'), last: col('last done'), next: col('next due'), assignee: col('assignee') }
  const billing = keys.findIndex((k, c) => c !== C.jobAddress && ADDRESS_HEADING.test(k))
  // The address the work is at: the Job Address, else the only address there is.
  const work = C.jobAddress >= 0 ? C.jobAddress : billing
  const cell = (r: string[], c: number): string => (c >= 0 ? (r[c] ?? '').trim() : '')
  // Case and spaces only: "Flat 1, 23 Park Road" and "Flat 12, 3 Park Road" stay apart.
  const norm = (v: string): string => v.toLowerCase().replace(/\s*,\s*/g, ', ').replace(/\s+/g, ' ').trim()
  const dateOf = (v: string): string | null => (v && !NO_VISIT.test(v) ? parseVisitDate(v, null, 'recent', today).date : null)
  const sameWhen = (a: string, b: string): boolean => !a || !b || (dateOf(a) ?? norm(a)) === (dateOf(b) ?? norm(b))
  const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const said = (v: string): string => { const d = dateOf(v); return d ? `${Number(d.slice(8))} ${MON[Number(d.slice(5, 7)) - 1]} ${d.slice(0, 4)}` : v }
  // Each different value once, as written (trimmed): "Gate 12 #34" and "Gate 1 #234" are both kept.
  const unique = (xs: string[]): string[] => xs.map((x) => x.trim()).filter((x, i, all) => x && all.indexOf(x) === i)
  const groups = new Map<string, number[]>()
  body.forEach((r, i) => {
    const ref = cell(r, C.ref)
    const key = ref ? `${ref}|${norm(cell(r, work) || cell(r, billing))}` : `#${i}`
    groups.set(key, [...(groups.get(key) ?? []), i])
  })
  const out: SqueegeeJobs = { body: [], lines: [], joined: [] }
  for (const idx of groups.values()) {
    const jobs = idx.map((i) => body[i])
    const days = jobs.map((j) => parseCycle(cell(j, C.frequency)))
    const known = days.filter((d): d is number => d !== null)
    const mainAt = known.length ? days.indexOf(Math.min(...known)) : 0
    const main = jobs[mainAt]
    // Each job joins only when its dates agree with EVERY job already in the visit, not just the main one:
    // an undated main job must not let jobs due 9 Oct and 16 Oct into one visit (review, 2 Oct).
    const together = [main]
    for (const j of jobs) {
      if (j === main || norm(cell(j, C.frequency)) !== norm(cell(main, C.frequency))) continue
      if (together.every((k) => sameWhen(cell(j, C.last), cell(k, C.last)) && sameWhen(cell(j, C.next), cell(k, C.next)))) together.push(j)
    }
    const apart = jobs.filter((j) => !together.includes(j))
    // The customer's own details from the main job, or the first job that has them.
    const row = header.map((_, c) => [main, ...jobs].map((j) => cell(j, c)).find(Boolean) ?? '')
    const set = (c: number, v: string): void => { if (c >= 0) row[c] = v }
    // Notes, and columns Squeegee is not known to have (an "Access notes" of their own): every different value.
    header.forEach((_, c) => { if (c === C.notes || (SQUEEGEE[keys[c]] === undefined && !ADDRESS_HEADING.test(keys[c]))) set(c, unique(jobs.map((j) => cell(j, c))).join('\n')) })
    set(C.frequency, cell(main, C.frequency))
    // Their prices added, when every one is one clear amount; otherwise all of them as written, which
    // the import shows as an unclear price for the owner to put right.
    const prices = together.map((j) => cell(j, C.price)).filter(Boolean)
    const read = prices.map((p) => readPrice(p))
    set(C.price, prices.length <= 1 ? prices[0] ?? '' : read.every((p) => p.pence !== null && p.clear) ? (read.reduce((a, p) => a + p.pence!, 0) / 100).toFixed(2) : prices.join(' + '))
    set(C.last, together.map((j) => cell(j, C.last)).find(Boolean) ?? '')
    set(C.next, together.map((j) => cell(j, C.next)).find(Boolean) ?? '')
    const services = unique(together.flatMap((j) => cell(j, C.services).split(/\s*,\s*/)))
    set(C.services, services.every((x) => /^window\s*clean(ing|s)?$/i.test(x)) ? '' : services.join(', '))
    set(C.assignee, unique(together.map((j) => cell(j, C.assignee))).join(', '))
    const notes = C.notes >= 0 ? row[C.notes].split('\n').filter(Boolean) : []
    // Where the cleaning is: the job's address. The customer's own, when different, is their billing address.
    if (work !== billing && work >= 0) {
      const own = cell(row, billing)
      if (!cell(row, work)) set(work, own)
      else if (own && norm(own) !== norm(cell(row, work))) notes.push(`Billing address: ${own}`)
    }
    for (const j of apart) {
      const p = readPrice(cell(j, C.price))
      const bits = [
        cell(j, C.services) || 'another job',
        p.pence !== null && p.clear ? `£${(p.pence / 100).toFixed(2)}` : cell(j, C.price),
        cell(j, C.frequency),
        dateOf(cell(j, C.last)) ? `last done ${said(cell(j, C.last))}` : '',
        dateOf(cell(j, C.next)) ? `next due ${said(cell(j, C.next))}` : '',
        cell(j, C.assignee) ? `by ${cell(j, C.assignee)}` : '',
      ]
      notes.push(`Also in Squeegee: ${bits.filter(Boolean).join(', ')}`)
    }
    set(C.notes, notes.join('\n'))
    out.body.push(row)
    out.lines.push(lines[idx[0]])
    if (jobs.length > 1) out.joined.push({ line: lines[idx[0]], name: cell(row, C.name), jobs: jobs.length })
  }
  return out
}

// ---- prices and how often ------------------------------------------------------

// "£20", "20.00", "20", "£20 inc gutters" → pence. Blank, "TBC", "free" → none.
export function parsePrice(text: string): number | null {
  // "£1,200.00" → 1200.00; "20,50" (a decimal comma) → 20.50.
  const t = text.replace(/(\d),(\d{3})(?!\d)/g, '$1$2').replace(/(\d),(\d{1,2})(?!\d)/g, '$1.$2')
  const m = t.match(/[£$]?\s*(\d+)(?:\.(\d{1,2}))?/)
  if (!m || m[1].length > 5) return null
  const pounds = Number(m[1])
  const pence = m[2] ? Number(m[2].padEnd(2, '0')) : 0
  const total = pounds * 100 + pence
  // "£0" or "0.00" written as such is a free clean; a blank cell is no price.
  if (total === 0) return /^\s*[£$]?\s*0+([.,]0{1,2})?\s*$/.test(text) ? 0 : null
  return total <= 1_000_000 ? total : null
}

// A price cell that does not say one clear amount (Astra review 28 Sep 2026):
// a minus figure (money owed, or a refund), pence ("20p"), or two amounts
// ("£20/£25", "20-25", "£20 + £5 conservatory"). None of these is read as a
// price; the words are kept in notes and the customer shows as Incomplete.
// With a £ in the cell only £ amounts count, so "£20 (4 weekly)" is £20.
export function readPrice(text: string): { pence: number | null; clear: boolean } {
  const t = text.trim()
  if (!t) return { pence: null, clear: true }
  if (/^[-−(]\s*[£$]?\s*\d/.test(t) || /[£$]\s*[-−]\s*\d/.test(t)) return { pence: null, clear: false }
  if (/^£?\s*\d+(\.\d+)?\s*p\b/i.test(t)) return { pence: null, clear: false }
  // "18?": the writer was not sure (set 4's notebook).
  if (/\?/.test(t)) return { pence: null, clear: false }
  // Two amounts side by side, with or without their £: "£20/25", "£20 + 5", "20 or 25".
  if (/\d\s*(\/|\+|&|–|-|\bor\b|\bto\b)\s*[£$]?\s*\d/i.test(t)) return { pence: null, clear: false }
  const plain = t.replace(/(\d),(\d{3})(?!\d)/g, '$1$2')
  const amounts = (/[£$]/.test(plain) ? plain.match(/[£$]\s*\d+(?:[.,]\d{1,2})?/g) : plain.match(/\d+(?:[.,]\d{1,2})?/g)) ?? []
  if (amounts.length > 1) return { pence: null, clear: false }
  const pence = parsePrice(/[£$]/.test(plain) && amounts[0] ? amounts[0] : t)
  return { pence, clear: pence !== null || amounts.length === 0 }
}

// "4 weekly", "every 8 weeks", "8w", "fortnightly", "monthly", "every 3
// months", "yearly" → days (the app's own choices use 28, 42, 56, 91, 122,
// 183 and 365). Anything else keeps its words only.
// monthly: a US or Canadian round, whose "monthly" is a calendar month (30, counted in months: round-config.ts
// cyclesOn; Step 2 round 2). A UK round's "monthly" stays 4 weeks.
const MONTHLY_DAYS = 30
export function parseCycle(text: string, monthly = false): number | null {
  const WORDS: Record<string, string> = { one: '1', two: '2', three: '3', four: '4', five: '5', six: '6', seven: '7', eight: '8', nine: '9', ten: '10', eleven: '11', twelve: '12' }
  const t = text.toLowerCase().replace(/\s+/g, ' ').trim().replace(/\b(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\b/g, (w) => WORDS[w])
    // The day it falls on says nothing about how often (Squeegee: "every week on Friday", "every month on the 3rd").
    .replace(/\s+on\s+(the\s+)?((mon|tues?|wed(nes)?|thu(rs)?|fri|sat(ur)?|sun)(day)?s?|\d{1,2}(st|nd|rd|th)?)(\s+of\s+the\s+month)?$/, '')
  if (!t) return null
  // A range ("6-8 weeks", "3/4 months", "4 or 6 weeks") and "every other month"
  // are not one cycle: kept as words, and the customer shows as Incomplete
  // (Astra review 28 Sep 2026: "6-8 weeks" was read as 8 weeks).
  if (/\d\s*(-|–|to|\/|or)\s*\d/.test(t)) return null
  if (/\b(every other|alternate|bi-?)\s*months?\b|\bbi-?monthly\b/.test(t)) return null
  if (/fortnight|\b(every other|alternate|every 2nd|every second) weeks?\b/.test(t)) return 14
  if (/\bquarter(ly)?\b/.test(t)) return 91
  if (/\b(twice a year|twice yearly|bi-?annual(ly)?|half[\s-]?yearly)\b/.test(t)) return 183
  if (/^(yearly|annual|annually|once a year|every year|12 months|1 year)$/.test(t)) return 365
  // Every few years, as patio and roof work comes round (15 Oct 2026): the app's own 2, 3 and 5 years.
  const years = t.match(/^(?:every\s+)?([235])\s*(?:years?|yrs?)$/)
  if (years) return years[1] === '2' ? 730 : years[1] === '3' ? 1095 : 1826
  const month = t.match(/(\d{1,2})\s*(?:-\s*)?(?:monthly|months?|mths?|mnths?)\b/)
  if (month) {
    const n = Number(month[1])
    return n === 1 ? (monthly ? MONTHLY_DAYS : 28) : n === 3 ? 91 : n === 4 ? 122 : n === 6 ? 183 : n === 12 ? 365 : n > 0 && n <= 24 ? Math.round(n * 30.4) : null
  }
  if (/^(monthly|every month|a month|once a month)$/.test(t)) return monthly ? MONTHLY_DAYS : 28
  const week = t.match(/(\d{1,2})\s*(?:-\s*)?(?:weekly|weeks?|wks?|w)\b/)
  if (week) {
    const n = Number(week[1])
    return n > 0 && n <= 52 ? n * 7 : null
  }
  if (/^(weekly|every week)$/.test(t)) return 7
  const days = t.match(/(\d{1,3})\s*(?:days?|d)\b/)
  if (days) {
    const n = Number(days[1])
    return n > 0 && n <= 400 ? n : null
  }
  const bare = t.match(/^(\d{1,2})$/)
  if (bare) {
    const n = Number(bare[1])
    return n > 0 && n <= 52 ? n * 7 : null
  }
  return null
}

// ---- building the customers ------------------------------------------------------

export interface BuiltCustomers {
  rows: ImportRow[]
  // For each of rows: its spreadsheet row number (the import screen keys the
  // owner's ticks and changes by it), and whether its name is really its
  // address (a sheet with no name column), so it is not "missing an address",
  // and whether its name is one the owner typed (opts.names), never matched
  // to a customer already on the round.
  // priceWords: the sheet's price cell when it did not say one clear amount
  // ("£20/£25"), so the import screen can show it.
  meta: { line: number; nameIsAddress: boolean; nameTyped: boolean; priceWords?: string }[]
  // One-cell rows that look like area headings (the import screen asks), used or not.
  lone: { line: number; text: string }[]
  // Rows left out, with the reason, numbered as the spreadsheet numbers them;
  // text: what the row said, for an area heading or a totals row.
  left: { line: number; reason: string; text?: string }[]
  // What the past-visit dates came to: how many customers got a last visit,
  // dates whose year was chosen (none in the cell or its heading), dates after
  // today (left out: not a past visit), dates that could not be read, and the
  // span of the last visits found.
  visits: { customers: number; yearless: number; future: number; unreadable: number; from: string | null; to: string | null }
}

// ---- past visit dates ----------------------------------------------------------

export type YearMode = 'recent' | number
const MONTHS3 = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
const monthNumber = (w: string): number | null => {
  const i = MONTHS3.indexOf(w.slice(0, 3).toLowerCase())
  return i >= 0 ? i + 1 : null
}
const fullYear = (y: string): number => (y.length === 2 ? 2000 + Number(y) : Number(y))
const iso = (y: number, m: number, d: number): string | null => {
  const dt = new Date(Date.UTC(y, m - 1, d))
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d ? `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}` : null
}

// The year a heading gives its column: "Job Date (Nov 25)" is 2025; "(Oct)" none.
export function headingYear(heading: string | null | undefined): number | null {
  return headingMonth(heading)?.y ?? null
}
function headingMonth(heading: string | null | undefined): { y: number; m: number } | null {
  const r = (heading ?? '').match(new RegExp(`\\b${MONTH_WORD}[\\s'.-]*(\\d{4}|\\d{2})\\b`, 'i'))
  const m = r ? monthNumber(r[1]!) : null
  return r && m ? { y: fullYear(r[2]!), m } : null
}

// For a date column with no year anywhere ("Job Date (Oct)"), the columns
// either side that have one ("Job Date (Nov 25)") say which year it is: a
// history runs in order, so "16 Sep" before Nov 2025 is Sep 2025, never last
// week (Liam's Enfield sheet, 28 Sep 2026).
export function columnBounds(header: string[] | null, dateCols: number[]): Map<number, { before: string | null; after: string | null }> {
  const out = new Map<number, { before: string | null; after: string | null }>()
  const anchors = dateCols.map((c) => ({ c, hm: headingMonth(header?.[c]) }))
  for (const { c, hm } of anchors) {
    if (hm) continue
    const right = anchors.find((a) => a.c > c && a.hm)?.hm ?? null
    const left = [...anchors].reverse().find((a) => a.c < c && a.hm)?.hm ?? null
    out.set(c, {
      before: right ? iso(right.y, right.m, new Date(Date.UTC(right.y, right.m, 0)).getUTCDate()) : null,
      after: left ? iso(left.y, left.m, 1) : null,
    })
  }
  return out
}

// Which way round "03/04" is read: day first (the UK, and a sheet that says so) or month first (a US or Canadian
// round's sheets; Step 2 round 2).
export type DateOrder = 'dmy' | 'mdy'

// What a sheet's own dates say: "25/03/2026" can only be day first, "03/25/2026" only month first. null when they do
// not say (every one could be either), or say both.
export function numericDateOrder(cells: string[]): DateOrder | null {
  let dayFirst = false
  let monthFirst = false
  for (const c of cells) {
    const r = c.trim().match(/^(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{2}|\d{4}))?$/)
    if (!r) continue
    const a = Number(r[1])
    const b = Number(r[2])
    if (a > 12 && b <= 12) dayFirst = true
    if (b > 12 && a <= 12) monthFirst = true
  }
  return dayFirst === monthFirst ? null : dayFirst ? 'dmy' : 'mdy'
}

// The cells written as numbers only ("03/04", "3.4.26"), from the visit-date and next-visit columns: the ones whose
// order matters.
export function numericDateCells(body: string[][], fields: ImportField[]): string[] {
  const cols = fields.map((f, c) => (f === 'visit_date' || f === 'next_visit' ? c : -1)).filter((c) => c >= 0)
  const out: string[] = []
  for (const row of body) for (const c of cols) {
    const v = (row[c] ?? '').trim()
    if (/^\d{1,2}[/.-]\d{1,2}(?:[/.-](\d{2}|\d{4}))?$/.test(v)) out.push(v)
  }
  return out
}

// One cell as a past-visit date, day first unless `order` says month first. The year comes from the
// cell, else from the column's heading, else from the owner's choice on the
// import screen: 'recent' is the latest such day on or before today.
export function parseVisitDate(cell: string, heading: string | null | undefined, mode: YearMode, today: string, bounds?: { before: string | null; after: string | null }, order: DateOrder = 'dmy'): { date: string | null; yearless: boolean } {
  const t = cell.trim().toLowerCase().replace(/(\d)(st|nd|rd|th)\b/g, '$1').replace(/,/g, ' ').replace(/\s+/g, ' ')
  let d: number | null = null
  let m: number | null = null
  let y: number | null = null
  let r: RegExpMatchArray | null
  // "2026-09-15", or with its time as apps export it ("2026-09-15T07:54:14+01:00"): the day as written.
  if ((r = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[t ]\d{1,2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:z|[+-]\d{2}:?\d{2})?)?$/))) { y = Number(r[1]); m = Number(r[2]); d = Number(r[3]) }
  else if ((r = t.match(/^(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{2}|\d{4}))?$/))) { d = Number(r[order === 'mdy' ? 2 : 1]); m = Number(r[order === 'mdy' ? 1 : 2]); y = r[3] ? fullYear(r[3]) : null }
  else if ((r = t.match(new RegExp(`^(\\d{1,2})[\\s/.-]*${MONTH_WORD}\\.?(?:[\\s/.-]*(\\d{4}|\\d{2}))?$`)))) { d = Number(r[1]); m = monthNumber(r[2]!); y = r[3] ? fullYear(r[3]) : null }
  else if ((r = t.match(new RegExp(`^${MONTH_WORD}\\.?[\\s/.-]*(\\d{1,2})(?:\\s+(\\d{4}|\\d{2}))?$`)))) { m = monthNumber(r[1]!); d = Number(r[2]); y = r[3] ? fullYear(r[3]) : null }
  if (d === null || m === null) return { date: null, yearless: false }
  if (y !== null) return { date: iso(y, m, d), yearless: false }
  const fromHeading = headingYear(heading)
  if (fromHeading !== null) return { date: iso(fromHeading, m, d), yearless: false }
  // The latest such day on or before the next column's month, or the first
  // on or after the previous column's month.
  if (bounds?.before) {
    const y0 = Number(bounds.before.slice(0, 4))
    const same = iso(y0, m, d)
    return { date: same && same <= bounds.before ? same : iso(y0 - 1, m, d), yearless: false }
  }
  if (bounds?.after) {
    const y0 = Number(bounds.after.slice(0, 4))
    const same = iso(y0, m, d)
    return { date: same && same >= bounds.after ? same : iso(y0 + 1, m, d), yearless: false }
  }
  if (mode !== 'recent') return { date: iso(mode, m, d), yearless: true }
  const thisYear = Number(today.slice(0, 4))
  const guess = iso(thisYear, m, d)
  return { date: guess && guess <= today ? guess : iso(thisYear - 1, m, d), yearless: true }
}

const clip = (s: string, n: number): string => (s.length > n ? s.slice(0, n) : s)
const NO_VISIT = /^(never|none|not\s*yet|n\/?a|-+|–|—)$/i

// `firstLine` is the spreadsheet row number of body[0], for the messages.
// `opts.names`: names the owner typed on the import screen, by spreadsheet
// row, so a row keeps its place when a column change leaves it with no name
// or address of its own (Astra review 28 Sep 2026).
// `opts.sections`: false when the owner said one-cell rows are customers, not area headings.
// `opts.lines`: each body row's spreadsheet row number, when they are not in one run.
export function buildCustomers(body: string[][], fields: ImportField[], firstLine: number, header: string[] | null = null, opts: { yearMode?: YearMode; today?: string; names?: Map<number, string>; sections?: boolean; lines?: number[]; dateOrder?: DateOrder; monthly?: boolean } = {}): BuiltCustomers {
  const today = opts.today ?? new Date().toISOString().slice(0, 10)
  const yearMode = opts.yearMode ?? 'recent'
  const dateOrder = opts.dateOrder ?? 'dmy'
  const visits: BuiltCustomers['visits'] = { customers: 0, yearless: 0, future: 0, unreadable: 0, from: null, to: null }
  const dateCols = fields.map((f, c) => (f === 'visit_date' ? c : -1)).filter((c) => c >= 0)
  const bounds = columnBounds(header, dateCols)
  // A how-often column whose heading says days ("Frequency (days)"): a bare
  // number there is days, not weeks.
  const inDays = fields.some((f, c) => f === 'cycle' && /day/i.test(header?.[c] ?? ''))
  const rows: ImportRow[] = []
  const meta: BuiltCustomers['meta'] = []
  const left: BuiltCustomers['left'] = []
  // An area written as a heading row above its customers ("Muswell Hill", then
  // the customers there): a row with one filled cell, on a sheet whose rows
  // are otherwise fuller, seen at least twice. It becomes those customers'
  // area when the sheet has no area column (held-out test 28 Sep 2026).
  // A lone row straight after another lone row is two sparse customers, not headings.
  const filledCount = (r: string[]): number => r.filter((v) => v.trim()).length
  const usual = body.map(filledCount).filter((n) => n >= 2).sort((a, b) => a - b)
  const median = usual.length ? usual[Math.floor(usual.length / 2)] : 0
  const totalsLabel = /^\s*(sub\s*-?\s*|grand\s*)?totals?(\s+(for|of)\b.*)?\s*(:|-|[£$][\d.,]*|\(.*\))?\s*$/i
  // A lone "TOTAL" at the end is a totals row, not an area heading.
  const loneAt = body.map((r, i) => (filledCount(r) === 1 && !totalsLabel.test(r.find((v) => v.trim()) ?? '') ? i : -1)).filter((i) => i >= 0)
  // UK numbers: some already start 0 or +44, or most are 10 digits starting 7 (mobiles without the 0).
  const phoneCells = body.flatMap((r) => r.filter((_, c) => fields[c] === 'phone')).map((v) => v.trim()).filter(Boolean)
  // On a UK sheet (some numbers kept their 0, or most lost it) a 10-digit
  // number gets its 0 back when it can only be a UK one: a mobile (7...), a
  // geographic number (1...: no US area code starts 1) or London (20...).
  // Any other, like a US "212 555 1234", only where no number kept its 0 and
  // most lost it (Codex review 28 Sep 2026). Excel keeps "07700 900123" as
  // written but drops the 0 from "02079460128", so one column mixes both.
  const ukSheet = phoneCells.some((v) => /^(0|\+44|0044)/.test(v))
  const noZero = phoneCells.filter((v) => /^[1237]\d{9}$/.test(v.replace(/\s/g, ''))).length
  const mostLost = !ukSheet && phoneCells.length > 0 && noZero >= phoneCells.length * 0.5
  const hasArea = fields.includes('area')
  const companyCol = header ? header.findIndex((h, c) => fields[c] !== 'name' && /^(company|business|firm|organi[sz]ation|trading\s*(name|as)|company\s*name|business\s*name)\b/i.test((h ?? '').trim())) : -1
  const customersSeen = body.filter((r) => filledCount(r) >= 2).length
  // opts.sections === false: the owner said these rows are customers.
  const could = !hasArea && median >= 3 && loneAt.length >= 2 && loneAt.length <= customersSeen && loneAt.every((i) => filledCount(body[i + 1] ?? []) >= 2)
  const sections = opts.sections !== false && could
  const loneSet = new Set(loneAt)
  let section = ''
  body.forEach((cells, i) => {
    const line = opts.lines?.[i] ?? firstLine + i
    // A blank row between groups of customers is not a customer, nor worth a mention.
    if (filledCount(cells) === 0) return
    // One cell, in the area column ("North cottages" above its customers, who
    // also say it): a heading for the area column, not a customer.
    const only = cells.findIndex((v) => v.trim())
    if (filledCount(cells) === 1 && fields[only] === 'area') {
      left.push({ line, reason: 'an area heading row', text: cells[only].trim() })
      return
    }
    if (sections && loneSet.has(i)) {
      section = cells.find((v) => v.trim())!.trim()
      left.push({ line, reason: 'an area heading', text: section })
      return
    }
    // A totals row ("TOTAL", "Subtotal", "Grand total") is not a customer.
    // Only the label and money or numbers beside it, so "Total Cleaning Ltd,
    // 4 High St, £25" is still a customer (Codex review 28 Sep 2026).
    const firstAt = cells.findIndex((v) => v.trim())
    const rest = cells.filter((v, c) => c !== firstAt && v.trim())
    // The label is the whole cell ("TOTAL", "Totals:", "Total for Enfield",
    // "TOTAL (regular prices)"), never a name that starts "Total".
    if (totalsLabel.test(cells[firstAt] ?? '') && rest.every((v) => /^\s*[£$]?\s*-?[\d.,]+\s*$/.test(v))) {
      left.push({ line, reason: 'a totals row', text: cells[firstAt].trim() })
      return
    }
    // The headings again (a second table under the first): not a customer.
    if (header) {
      const same = header.filter((h, c) => h.trim() && (cells[c] ?? '').trim().toLowerCase() === h.trim().toLowerCase()).length
      if (same >= 2 && same >= header.filter((h) => h.trim()).length * 0.6) {
        left.push({ line, reason: 'the headings again' })
        return
      }
    }
    const parts: Partial<Record<ImportField, string[]>> = {}
    cells.forEach((v, c) => {
      const f = fields[c] ?? 'skip'
      if (f === 'skip' || v === '') return
      // A column put into notes keeps its heading, so "£10.00" arrives as
      // "Skylights: £10.00" and "Website" as "Job Origin: Website" (28 Sep 2026).
      const h = (header?.[c] ?? '').trim()
      const labelled = (f === 'notes' || f === 'job_notes') && h && !/^(notes?|comments?|info|information|remarks?|details|job\s*notes|access(\s*notes)?)$/i.test(h) ? `${h}: ${v.trim()}` : v
      ;(parts[f] ??= []).push(labelled)
    })
    const joined = (f: ImportField, sep: string): string => (parts[f] ?? []).join(sep).trim()
    let name = joined('name', ' ') || [joined('first_name', ' '), joined('last_name', ' ')].filter(Boolean).join(' ')
    // A postcode at the end of any address part ("Leeds LS6 1QA" before "7 Mill
    // Lane") is lifted out of it when there is no postcode column (Codex review).
    const addressParts = (parts.address ?? []).map((v) => v.trim()).filter(Boolean)
    let lifted = ''
    if (!joined('postcode', ' ')) {
      for (let k = 0; k < addressParts.length && !lifted; k++) {
        const pm = addressParts[k].match(/,?\s*([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})\s*$/i)
        // ...or at the start of one ("LS6 1QA Leeds").
        const ps = addressParts[k].match(/^\s*([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})\b[,\s]*/i)
        if (pm && k < addressParts.length - 1) {
          lifted = pm[1].toUpperCase()
          addressParts[k] = addressParts[k].slice(0, pm.index).replace(/[,\s]+$/, '')
        } else if (ps && addressParts[k].length > ps[0].length) {
          lifted = ps[1].toUpperCase()
          addressParts[k] = addressParts[k].slice(ps[0].length).trim()
        }
      }
    }
    let address = addressParts.filter(Boolean).join(', ')
    // Name and address in one cell ("Anna Smith — 7 Mill Lane, Leeds, LS6 1QA",
    // "Anna Smith, 7 Mill Lane"): split when what follows reads like an address.
    if (name && !address) {
      const addressLike = (x: string): boolean => /^((flat|unit|apt)\s*\w+,?\s*)?\d+[a-z]?\s+\p{L}/iu.test(x) || /[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}\s*$/i.test(x)
      const m = name.match(/^(.{2,80}?)\s+[—–-]\s+(.+)$/) ?? name.match(/^([^,\d]{2,80}),\s*(.+)$/)
      if (m && addressLike(m[2].trim())) {
        name = m[1].trim()
        address = m[2].trim()
      }
    }
    let postcode = joined('postcode', ' ').toUpperCase() || lifted
    // An address that ends with the postcode already in its own column: said once (Astra audit).
    if (postcode && address) {
      const tail = new RegExp(`[,\\s]*${postcode.replace(/\s+/g, '\\s*')}\\s*$`, 'i')
      if (tail.test(address)) address = address.replace(tail, '')
    }
    // A postcode sitting at the end of the address, with no postcode column.
    if (!postcode && address) {
      const m = address.match(/,?\s*([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})\s*$/i)
      if (m) {
        postcode = m[1].toUpperCase()
        address = address.slice(0, m.index).replace(/[,\s]+$/, '')
      }
    }
    // No name on this row: the business name, when the sheet has one, before the address (set 4: "Corona Cafe").
    if (!name && companyCol >= 0 && (cells[companyCol] ?? '').trim()) name = cells[companyCol].trim()
    const nameIsAddress = !name && !!address
    if (!name) name = address
    const nameTyped = !name && !!opts.names?.get(line)?.trim()
    if (!name) name = opts.names?.get(line)?.trim() ?? ''
    if (!name) {
      left.push({ line, reason: 'no name or address' })
      return
    }
    const row: ImportRow = { name: clip(name, 200) }
    if (address && address !== name) row.address = clip(address, 500)
    if (postcode) row.postcode = clip(postcode, 20)
    // A number that lost its leading 0 in Excel ("7700 900123", "2079460128"),
    // only on a sheet whose numbers are UK ones (Codex review: a US number stays).
    const withZero = (v: string): string => {
      const d = v.replace(/\s/g, '')
      if (!/^[1237]\d{9}$/.test(d)) return v
      return (/^(7|1|20)/.test(d) ? ukSheet || mostLost : mostLost) ? `0${v.trim()}` : v
    }
    const phone = (parts.phone ?? []).map(withZero).join(' / ').trim()
    if (phone) row.phone = clip(phone, 100)
    // "Estate / area", "Neighbourhood": part of the address, and the area too
    // when the sheet has no area column (Astra audit: both meanings kept).
    const areaInAddress = !hasArea && header ? header.findIndex((h, c) => fields[c] === 'address' && /\b(area|estate|neighbou?rhood|district|patch)\b/i.test(h ?? '')) : -1
    const area = joined('area', ' ') || (sections ? section : '') || (areaInAddress >= 0 ? (cells[areaInAddress] ?? '').trim() : '')
    if (area) row.area = clip(area, 200)
    const preferred = joined('preferred_times', '; ')
    if (preferred) row.preferred_times = clip(preferred, 500)
    // The latest past visit among the visit-date columns, with that day's
    // price when the column right after it is a price ("Price on the day").
    let last: { date: string; c: number } | null = null
    for (const c of dateCols) {
      const cell = (cells[c] ?? '').trim()
      // "never" (Squeegee's Last Done for a new customer), "none", "-": no visit yet, not a date Roundly couldn't read.
      if (!cell || NO_VISIT.test(cell)) continue
      const got = parseVisitDate(cell, header?.[c], yearMode, today, bounds.get(c), dateOrder)
      if (!got.date) { visits.unreadable++; continue }
      if (got.yearless) visits.yearless++
      if (got.date > today) { visits.future++; continue }
      if (!last || got.date > last.date) last = { date: got.date, c }
    }
    if (last) {
      const lv: { date: string; amount_pence?: number } = { date: last.date }
      const next = last.c + 1
      if (fields[next] !== 'price' && /price|£|\$|amount|charge|paid/i.test(header?.[next] ?? '')) {
        const p = readPrice((cells[next] ?? '').trim()).pence
        if (p !== null) lv.amount_pence = p
      }
      row.last_visit = lv
      visits.customers++
      if (!visits.from || last.date < visits.from) visits.from = last.date
      if (!visits.to || last.date > visits.to) visits.to = last.date
    }
    // The customer's own Google Maps pin, when the sheet has one: tapping their
    // address on their page opens it (28 Sep 2026). Only a real https link.
    const mapsLink = (parts.maps_link ?? []).map((v) => v.trim()).find((v) => MAPS_LINK_RE.test(v))
    if (mapsLink) row.maps_link = clip(mapsLink, 1000)
    const priceText = joined('price', ' ')
    const price = readPrice(priceText)
    const pence = price.pence
    if (pence !== null) row.reg_price_pence = pence
    // Words with the amount ("20 fronts only") are kept too.
    const priceWords = !!priceText && (pence === null || /[a-z]{2,}/i.test(priceText.replace(/\b(gbp|usd|cad|each|ea)\b/gi, '')))
    const cycleText = joined('cycle', ' ')
    if (cycleText) {
      const bareDays = inDays && /^\d{1,3}$/.test(cycleText.trim()) ? Number(cycleText.trim()) : null
      const days = bareDays !== null && bareDays > 0 ? bareDays : parseCycle(cycleText, opts.monthly ?? false)
      if (days !== null) row.cycle_days = days
      else row.cycle_text = clip(cycleText, 200)
    }
    // The next visit to book: a date today or later ("30 Oct" with no year is the next 30 Oct). One that has
    // passed, or can't be read, is not booked: it is kept in notes, and the customer shows as due as before.
    // The worker goes with the booking; with no booking it is kept in notes.
    const unbooked: string[] = []
    const nextAt = fields.indexOf('next_visit')
    const nextCell = nextAt >= 0 ? (cells[nextAt] ?? '').trim() : ''
    const workerText = joined('worker', ', ')
    let nextDate: string | null = null
    if (nextCell && !NO_VISIT.test(nextCell)) {
      const got = parseVisitDate(nextCell, header?.[nextAt], 'recent', today, undefined, dateOrder)
      nextDate = got.date
      if (got.date && got.yearless && got.date < today) nextDate = iso(Number(got.date.slice(0, 4)) + 1, Number(got.date.slice(5, 7)), Number(got.date.slice(8)))
      if (nextDate && nextDate >= today) row.next_visit = { date: nextDate, ...(workerText ? { worker: clip(workerText, 100) } : {}) }
      else unbooked.push(`${(header?.[nextAt] ?? '').trim() || 'Next visit'}: ${nextCell}`)
    }
    if (workerText && !row.next_visit) unbooked.push(`${(header?.[fields.indexOf('worker')] ?? '').trim() || 'Worker'}: ${workerText}`)
    const jobNotes = joined('job_notes', '\n')
    if (jobNotes) row.job_notes = clip(jobNotes, 2000)
    // The first email that is one plain address goes in their Email (0036), so email can be how they are
    // messaged; any other address in the row is kept in notes as before.
    const emails = (parts.email ?? []).map((e) => e.trim()).filter(Boolean)
    const firstEmail = emails.find((e) => IMPORT_EMAIL_RULE.test(e))
    if (firstEmail) row.email = clip(firstEmail, 200)
    const notes = [
      priceWords ? `Price: ${priceText}` : '',
      ...emails.filter((e) => e !== firstEmail).map((e) => `Email: ${e}`),
      ...unbooked,
      joined('notes', '\n'),
    ].filter(Boolean).join('\n')
    if (notes) row.notes = clip(notes, 2000)
    rows.push(row)
    meta.push({ line, nameIsAddress, nameTyped, ...(price.clear ? {} : { priceWords: clip(priceText, 60) }) })
  })
  const lone = could ? loneAt.map((i) => ({ line: opts.lines?.[i] ?? firstLine + i, text: body[i].find((v) => v.trim())!.trim() })) : []
  return { rows, meta, left, visits, lone }
}

// ---- checking the customers before they come in ----------------------------------

// What a customer coming in is missing (Liam 28 Sep 2026: "maybe need a
// incomplete customers"): somewhere to find them (an address or a Google Maps
// pin), a phone number to message them, a price, and how often. "Adhoc", "on
// request", "one off" and the like are an answer, not a gap: the same answers
// cycleAgreed in planning.ts accepts, kept here so this file stays standalone.
export type MissingDetail = 'address' | 'phone' | 'price' | 'cycle'
const AGREED_NO_CYCLE = /^(when[\s-]?they[\s-]?ask|ad[\s-]?hoc|on[\s-]?request|as[\s-]?and[\s-]?when|when[\s-]?required|when[\s-]?needed|on[\s-]?call|on[\s-]?demand|when[\s-]?asked|as[\s-]?(needed|required)|one[\s-]?off|just[\s-]?once|doesn.?t want|not wanted)/i
export function missingDetails(r: ImportRow, nameIsAddress = false): MissingDetail[] {
  const out: MissingDetail[] = []
  if (!nameIsAddress && !r.address?.trim() && !r.maps_link) out.push('address')
  if (!r.phone?.trim()) out.push('phone')
  if (r.reg_price_pence === undefined) out.push('price')
  if (!(r.cycle_days && r.cycle_days > 0) && !AGREED_NO_CYCLE.test((r.cycle_text ?? '').trim())) out.push('cycle')
  return out
}

// A sheet whose newest visit is weeks old was probably not kept up to date
// (Liam 28 Sep 2026: "we havent been keeping up to date weve just been doing
// them as and when they ask"), and its last visits would make everyone look
// overdue. Six weeks: on a round that is being worked, somebody was cleaned
// more recently than that.
export const OLD_SHEET_DAYS = 42
const dayNumber = (iso: string): number => Date.parse(`${iso}T00:00:00Z`) / 86_400_000
export function sheetLooksOld(newest: string | null, today: string): boolean {
  return !!newest && dayNumber(today) - dayNumber(newest) > OLD_SHEET_DAYS
}

// "5 days ago", "3 weeks ago", "7 months ago", "2 years ago".
export function howLongAgo(date: string, today: string): string {
  const days = Math.round(dayNumber(today) - dayNumber(date))
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 14) return `${days} days ago`
  if (days < 61) return `${Math.round(days / 7)} weeks ago`
  const months = Math.round(days / 30.44)
  if (months < 24) return `${months} months ago`
  return `${Math.round(days / 365.25)} years ago`
}

// The same customer already on the round: name and postcode (or name and
// address when there is no postcode), ignoring case and spaces.
// Every house and flat number in the address counts too, in any order, so two
// Smiths at 12 and 14 in one postcode stay two customers, and so do a landlord's
// "23 Park Road, Flat 1" and "23 Park Road, Flat 2" (review 2 Oct 2026: only the
// first number counted, and the second flat was taken for the first). Only the
// numbers, not the whole address, so the same house written with or without its
// town ("17 Crescent Rise" / "17 Crescent Rise, Muswell Hill") is still the
// same customer; a postcode written in the address is not a house number.
// Letters in any alphabet count.
export function sameCustomerKey(c: { name?: string | null; postcode?: string | null; address?: string | null }): string {
  const squash = (s: string): string => s.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '')
  const n = squash(c.name ?? '')
  const bare = (c.address ?? '').toLowerCase().replace(/\b[a-z]{1,2}\d[a-z\d]?\s*\d[a-z]{2}\b/g, ' ')
  const house = [...bare.matchAll(/\d+[a-z]?\b|\b(?:flat|unit|apt|apartment|suite|room)\s*([a-z])\b/g)].map((x) => x[1] ?? x[0]).sort().join(',')
  const where = (c.postcode ?? '').toLowerCase().replace(/\s/g, '') || squash(c.address ?? '').slice(0, 20)
  return `${n}|${where}|${house}`
}

// Which sheet of a workbook holds the customers: the one whose headings read
// most like a customer list, then the one with most rows.
export function sheetScore(rows: string[][]): number {
  const h = findHeaderRow(rows)
  const header = h >= 0 ? rows[h] : []
  const found = new Set(header.map((c) => headingField(c)).filter((f): f is ImportField => f !== null))
  const key = ['name', 'first_name', 'last_name', 'address', 'postcode', 'phone'].filter((f) => found.has(f as ImportField)).length
  return key * 100000 + rows.length
}
